/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

// Frozen so every "nothing to report" result is the same reference and a memo built on one of
// these does not re-run on each render. `EMPTY_KEY_SET` can't be frozen the same way — `Object.
// freeze` doesn't stop `Set#add` — but nothing here ever mutates it, so one shared instance is
// safe and avoids allocating a new empty Set on every call.
const EMPTY_LIST = Object.freeze([]);
const EMPTY_KEY_SET = new Set();

/**
 * @description The profiles worth offering for the instance roles currently in the form.
 * Inactive ones are left out: they cannot be picked any more. One that is already assigned is
 * added back by the caller, so a user never loses a profile just because it was deactivated.
 * @param profiles Normalized profiles from `usePermissionProfiles`.
 * @param roles The instance roles chosen in the form, e.g. `['admin']`.
 * @returns The matching profiles, in the order given.
 */
export const filterProfilesForRoles = (profiles, roles) => {
  if (!Array.isArray(profiles) || !Array.isArray(roles) || roles.length === 0) return EMPTY_LIST;

  const chosenRoles = new Set(roles);

  return profiles.filter(
    (profile) =>
      profile?.active !== false &&
      Array.isArray(profile?.applicable_roles) &&
      profile.applicable_roles.some((role) => chosenRoles.has(role))
  );
};

/**
 * @description How the ticked modules compare with the profile the user is on, and the one-line
 * label describing it.
 *
 * Divergence is measured against the profile's *grantable* modules only. A profile may grant a
 * module this particular user cannot be given — a group-only one, say — and counting that as
 * removed would leave the form permanently reading as modified over a box nobody can tick.
 *
 * @param profile The assigned profile, or null for a hand-picked selection.
 * @param selectedKeys The module keys currently ticked.
 * @param grantableKeySet Every key that may be ticked for this user.
 * @param fallbackCustomized Used only when the profile's own module list is unknown: the flag the
 *   server already stored then decides whether the label says "modified".
 * @returns `{ label, addedKeys, removedKeys, profileKeySet, diverged }`. `profileKeySet` is null
 *   when there is nothing to compare against, which is what suppresses the per-module markers.
 */
export const buildModuleAccessSummary = ({
  profile = null,
  selectedKeys = EMPTY_LIST,
  grantableKeySet = EMPTY_KEY_SET,
  fallbackCustomized = false,
} = {}) => {
  const grantable = grantableKeySet instanceof Set ? grantableKeySet : EMPTY_KEY_SET;
  const selected = (Array.isArray(selectedKeys) ? selectedKeys : EMPTY_LIST).filter((key) =>
    grantable.has(key)
  );
  const countText = `${selected.length} of ${grantable.size} selected`;

  if (!profile) {
    return {
      label: `Custom — ${countText}`,
      addedKeys: EMPTY_LIST,
      removedKeys: EMPTY_LIST,
      profileKeySet: null,
      diverged: false,
    };
  }

  const name = profile.name || 'Profile';

  // A missing `module_keys` means the profile itself was not in the page of profiles held here,
  // so there is nothing to diff and the markers are skipped rather than guessed at.
  if (!Array.isArray(profile.module_keys)) {
    return {
      label: fallbackCustomized ? `${name} · modified — ${countText}` : `${name} — ${countText}`,
      addedKeys: EMPTY_LIST,
      removedKeys: EMPTY_LIST,
      profileKeySet: null,
      diverged: fallbackCustomized,
    };
  }

  const profileKeySet = new Set(profile.module_keys.filter((key) => grantable.has(key)));
  const selectedSet = new Set(selected);
  const addedKeys = selected.filter((key) => !profileKeySet.has(key));
  const removedKeys = Array.from(profileKeySet).filter((key) => !selectedSet.has(key));
  const diverged = addedKeys.length > 0 || removedKeys.length > 0;

  return {
    label: diverged
      ? `${name} · modified — ${countText} (+${addedKeys.length}, −${removedKeys.length})`
      : `${name} — ${countText}`,
    addedKeys,
    removedKeys,
    profileKeySet,
    diverged,
  };
};
